import useUserEditVersion from '@/features/config/hooks/useUserEditVersion'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type MutableRefObject,
} from 'react'
import { etValidToPayload } from './etValidTo'
import toast from 'react-hot-toast'
import { getApiErrorMessage, isRetryableApiError } from '@/helpers/notify'
import {
  mergeKnownRows,
  retireCreatedDrafts,
  retireSavedEdits,
  splitEditsForSave,
  withoutKey,
} from '@/utils/stagedSave'
import { useSelector } from 'react-redux'
import { Box } from '@mui/material'
import etReferenceDataConfig from '@/api/config/etReferenceDataConfig'
import DeleteIconButton from '@/features/config/components/common/DeleteIconButton'
import { usePermission } from '@/hooks/permissions'
import { useUnits } from '@/hooks/useUnits'
import useTranslation from '@/hooks/useTranslation'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import DataGrid, {
  gridCellClasses,
  gridRowClasses,
} from '@/components/grid/DataGrid'
import useGuidedAutosave from '@/features/config/hooks/useGuidedAutosave'
import useSerialisedSave from '@/features/config/hooks/useSerialisedSave'
import { useIsGuidedSetup } from '@/features/config/contexts/guided-edit-context'
import { toMetricValue, unitValueGetter } from '@/components/grid/unitColumn'
import {
  commitOrHoldOpenEditor,
  type GuidedSaveOptions,
} from '@/components/grid/commitOpenEditor'
import {
  ET_MONTH_MAX_MM,
  ET_MONTH_MIN_MM,
  invalidEtMonths,
} from './etMonthValidation'
import type { GridApi, GridCellCtx, GridColumn } from '@/components/grid/types'

// ET reference writes must be serialised: the config APIs return 500 under
// concurrent writes, the same as the plant and decoder grids.
const SAVE_RETRY_LIMIT = 3
const SAVE_RETRY_DELAY_MS = 300
const SAVE_REQUEST_GAP_MS = 120

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * The permanent "+ Add ET Reference" affordance. It holds no data and is never
 * written.
 */
const ADD_ROW_ID = '__CREATE_ROW__'

/**
 * Draft ids come from a counter seeded here and decremented per draft, so any
 * number of drafts can be staged at once without colliding. Negative throughout:
 * the rest of the grids already read a negative id as "not on the server yet".
 */
const DRAFT_ID_START = -1_000_000
const isDraftId = (id: string | number) => String(id).startsWith('-')

// The API rejects an empty or ISO-shaped validity date; the drawer sends this
// shape, so the grid matches it. A new reference has no window yet, so it gets
// the current time rather than a blank the endpoint would 400 on. In UTC, both
// ways: see etValidTo.
const formatValidTo = (value?: string) => etValidToPayload(value)

const MONTH_FIELDS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const

type MonthField = (typeof MONTH_FIELDS)[number]

const MONTH_LABEL_KEYS: Record<MonthField, string> = {
  january: 'shortMonthJan',
  february: 'shortMonthFeb',
  march: 'shortMonthMar',
  april: 'shortMonthApr',
  may: 'shortMonthMay',
  june: 'shortMonthJun',
  july: 'shortMonthJul',
  august: 'shortMonthAug',
  september: 'shortMonthSep',
  october: 'shortMonthOct',
  november: 'shortMonthNov',
  december: 'shortMonthDec',
}

export interface EtReferenceRow {
  id: string | number
  description: string
  remoteETperDay: number
  remoteETValidTo: string
  isUsed?: boolean
  january: number
  february: number
  march: number
  april: number
  may: number
  june: number
  july: number
  august: number
  september: number
  october: number
  november: number
  december: number
}

/**
 * Edited fields of a row, already converted to mm. The conversion happens where
 * the edit is staged, not on save, so this and the untouched row fields hold the
 * same unit and the payload builder never has to guess which is which.
 */
type PendingEdit = Partial<Record<MonthField | 'description', string | number>>

/** A create staged in the grid; nothing is POSTed until the toolbar Save runs. */
interface DraftRow {
  id: string
  values: PendingEdit
}

export interface EtReferenceGridActions {
  save: () => Promise<{ failed: number; total: number }>
  cancel: () => void
  refresh: () => void
  hasPendingEdits: () => boolean
  /** Drops a row's staged edit once the row has been deleted. */
  forgetRow: (id: string) => void
}

interface EtReferenceTableProps {
  interfaceId: number
  keyword?: string
  bulkEditMode?: boolean
  onDeleteItem?: (id: string, name?: string) => void
  onRowCountChange?: (count: number) => void
  actionsRef: MutableRefObject<EtReferenceGridActions | null>
}

const EtReferenceTable: FC<EtReferenceTableProps> = ({
  interfaceId,
  keyword = '',
  bulkEditMode = false,
  onDeleteItem,
  onRowCountChange,
  actionsRef,
}) => {
  const { t } = useTranslation()
  const { isViewOnly } = usePermission()
  const {
    isMetric,
    convertFromMetricV2,
    convertToMetricV2,
    getImperialUnitLabel,
  } = useUnits()
  // The converting column helper takes just these three, so they are pinned to
  // the measurement system rather than the hook, whose closures are new on
  // every render. Same shape as the station grid.
  const units = useMemo(
    () => ({ convertFromMetricV2, convertToMetricV2, getImperialUnitLabel }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMetric],
  )
  // ET is stored in mm and shown in the user's unit, but the headers were bare
  // month names — nothing told an imperial user whether the figures were
  // millimetres or inches.
  const etUnit = getImperialUnitLabel('mm')
  const isDark = useSelector(selectTheme) === mode_type.dark
  const gridApiRef = useRef<GridApi<EtReferenceRow> | null>(null)

  // Pending edits live here rather than on the rows, so a refetch cannot throw
  // away unsaved work.
  const [pendingEdits, setPendingEdits] = useState<Map<string, PendingEdit>>(
    new Map(),
  )
  const [userEditVersion, noteUserEdit] = useUserEditVersion()
  // The ref is the source of truth and is written the moment an edit is
  // staged, not on render: a save that starts with an editor open reads it
  // straight after `commitOpenEditor`, before React has rendered the state the
  // editor's commit set — reading a render-assigned ref missed that edit.
  const pendingEditsRef = useRef(pendingEdits)
  const updatePendingEdits = useCallback(
    (update: (prev: Map<string, PendingEdit>) => Map<string, PendingEdit>) => {
      const next = update(pendingEditsRef.current)
      if (next === pendingEditsRef.current) return
      pendingEditsRef.current = next
      setPendingEdits(next)
    },
    [],
  )

  // Staged creates, in the order they were added. They live outside `rows` so a
  // refetch — which replaces the server rows — cannot drop them; `rowData`
  // splices them back in for display.
  const [drafts, setDrafts] = useState<DraftRow[]>([])
  const draftsRef = useRef(drafts)
  draftsRef.current = drafts
  const draftIdRef = useRef(DRAFT_ID_START)

  // Drafts a save refused to write, so the offending cell can be marked.
  const [invalidRowIds, setInvalidRowIds] = useState<Set<string>>(new Set())
  // Month cells a save refused: out of 0–65 mm, or text that is not a number.
  // Keyed `${rowId}::${month}` (SCRUM-1032 round 7, item 9).
  const [invalidMonthCells, setInvalidMonthCells] = useState<Set<string>>(
    new Set(),
  )
  // SCRUM-1032: inside the guided setup this list writes itself once the user
  // pauses, like the decoder and pipe lists. On /config Save is the contract.
  const isGuidedSetup = useIsGuidedSetup()

  const [rows, setRows] = useState<EtReferenceRow[]>([])
  // Every row fetched, kept across a search so an edit on a row the search
  // hides is still sent (see splitEditsForSave). `listCompleteRef` says the
  // last fetch had no search, so a row missing from it has been deleted.
  const knownRowsRef = useRef<Map<string, EtReferenceRow>>(new Map())
  const listCompleteRef = useRef(false)
  // Only the FIRST fetch draws skeleton rows. A refetch after a save or a
  // keyword change keeps the rows that are already on screen, so the list
  // never blinks out from under the user.
  const [loadedOnce, setLoadedOnce] = useState(false)

  // Months start undefined rather than 0: a grid full of zeros before the user
  // has typed anything reads as real data. The payload builder turns a missing
  // value into 0 on save.
  const blankRow = useCallback(
    (): EtReferenceRow =>
      ({
        id: ADD_ROW_ID,
        description: '',
        remoteETperDay: 0,
        remoteETValidTo: '',
        ...Object.fromEntries(MONTH_FIELDS.map((month) => [month, undefined])),
      }) as unknown as EtReferenceRow,
    [],
  )

  // A config list of this size is fetched whole: it keeps the create row as a
  // real last row and lets the grid sort without a server round trip.
  const fetchRows = useCallback(async () => {
    try {
      const res: any = await etReferenceDataConfig.getAll({
        interfaceId,
        PageIndex: 1,
        PageSize: 0,
        ...(keyword ? { Keyword: keyword } : undefined),
      } as any)
      const list: EtReferenceRow[] = res?.data?.data ?? []
      knownRowsRef.current = mergeKnownRows(knownRowsRef.current, list, !keyword)
      listCompleteRef.current = !keyword
      setRows(list)
      onRowCountChange?.(res?.data?.totalCount ?? list.length)
    } catch (error: any) {
      toast.error(error?.message || t('somethingWentWrong'))
    } finally {
      setLoadedOnce(true)
    }
  }, [interfaceId, keyword, onRowCountChange, t])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  const refresh = useCallback(() => {
    fetchRows()
  }, [fetchRows])

  // The chip stays put once a draft has been added, so several references can be
  // staged one after another.
  const addRow = useMemo<EtReferenceRow | null>(
    () => (bulkEditMode && !isViewOnly ? blankRow() : null),
    [bulkEditMode, isViewOnly, blankRow],
  )

  // Leaving bulk edit ends the staged batch, whichever way it ended.
  useEffect(() => {
    if (bulkEditMode) return
    draftsRef.current = []
    setDrafts([])
    setInvalidRowIds(new Set())
    setInvalidMonthCells(new Set())
    draftIdRef.current = DRAFT_ID_START
  }, [bulkEditMode])

  const rowData = useMemo(() => {
    const merged = rows.map((row) => {
      const edit = pendingEdits.get(String(row.id))
      // Edits are held in display units and may be mid-typing strings; the
      // payload builder coerces them back on save.
      return edit ? ({ ...row, ...edit } as EtReferenceRow) : row
    })
    const staged = drafts.map(
      (draft) =>
        ({ ...blankRow(), id: draft.id, ...draft.values }) as EtReferenceRow,
    )
    const all = [...merged, ...staged]
    return addRow ? [...all, addRow] : all
  }, [rows, pendingEdits, drafts, addRow, blankRow])

  const getRowId = useCallback((row: EtReferenceRow) => String(row.id), [])

  // Keep the staged drafts and the "+ Add ET Reference" chip below the saved
  // rows regardless of sort, in the order they were added.
  const postSort = useCallback((list: EtReferenceRow[]) => {
    const chip = list.find((row) => row.id === ADD_ROW_ID)
    const rest = list.filter((row) => row.id !== ADD_ROW_ID)
    const staged = rest
      .filter((row) => isDraftId(row.id))
      .sort((a, b) => Number(b.id) - Number(a.id))
    const merged = [...rest.filter((row) => !isDraftId(row.id)), ...staged]
    return chip ? [...merged, chip] : merged
  }, [])

  // The update endpoint replaces the record, so every field the row owns has to
  // be sent back — not just the edited ones. Staged values are already in mm:
  // the conversion happens where the edit is staged, so both branches below are
  // metric and neither is converted again here. Converting a second time is
  // what turned a saved 52 into 52.02.
  const buildPayload = useCallback(
    (row: EtReferenceRow, edit: PendingEdit) => {
      const months = Object.fromEntries(
        MONTH_FIELDS.map((month) => [
          month,
          month in edit
            ? Number(edit[month] ?? 0) || 0
            : Number(row[month] ?? 0),
        ]),
      ) as Record<MonthField, number>

      return {
        interfaceId: Number(interfaceId),
        description: String(edit.description ?? row.description ?? '').trim(),
        // Not shown in the grid — the drawer owns them — but the endpoint
        // replaces the record and validates both, so they are always sent:
        // the row's own values on an update, and 0 / today for a new row.
        remoteETperDay: Number(row.remoteETperDay ?? 0),
        remoteETValidTo: formatValidTo(row.remoteETValidTo),
        ...months,
      }
    },
    [interfaceId],
  )

  // --- Staged drafts --------------------------------------------------------

  // The row is only in the DOM after the drafts state has rendered, so the
  // editor waits a tick.
  const startEditingRow = useCallback((rowId: string) => {
    setTimeout(() => {
      gridApiRef.current?.scrollToRow(rowId)
      gridApiRef.current?.startEditingCell(rowId, 'description')
    }, 0)
  }, [])

  const handleAddDraft = useCallback(() => {
    if (isViewOnly) return
    gridApiRef.current?.stopEditing()
    const id = String(draftIdRef.current)
    draftIdRef.current -= 1
    const next = [...draftsRef.current, { id, values: {} }]
    draftsRef.current = next
    setDrafts(next)
    noteUserEdit()
    startEditingRow(id)
  }, [isViewOnly, startEditingRow, noteUserEdit])

  /** Drops a single staged draft, leaving the rest of the batch alone. */
  const handleDiscardDraft = useCallback((id: string) => {
    const next = draftsRef.current.filter((draft) => draft.id !== id)
    draftsRef.current = next
    setDrafts(next)
    setInvalidRowIds((prev) => {
      if (!prev.has(id)) return prev
      const remaining = new Set(prev)
      remaining.delete(id)
      return remaining
    })
    setInvalidMonthCells((prev) => {
      const remaining = new Set(
        [...prev].filter((key) => !key.startsWith(`${id}::`)),
      )
      return remaining.size === prev.size ? prev : remaining
    })
  }, [])

  const handleCellValueChanged = useCallback(
    (event: {
      row: EtReferenceRow
      field: string
      oldValue: unknown
      newValue: unknown
    }) => {
      noteUserEdit()
      const field = event.field as MonthField | 'description'
      if (!field) return

      const rowId = String(event.row.id)

      // Months are typed in the user's unit and stored in mm, so they are
      // converted back at the point they are staged — this grid stages its own
      // edits and returns false below, which means a column valueSetter would
      // never run. Everything downstream (drafts, pendingEdits, the payload)
      // therefore holds metric, exactly like the untouched rows.
      //
      // toMetricValue keeps the stored number when the displayed one is typed
      // back unchanged: display rounds to 2dp, so converting unconditionally
      // would drift the value a little on every visit to the editor.
      const isMonth = (MONTH_FIELDS as readonly string[]).includes(field)

      // A new value for a month the last save refused is the user's fix; the
      // mark comes off and the next save judges it afresh.
      if (isMonth) {
        const key = `${rowId}::${field}`
        setInvalidMonthCells((prev) => {
          if (!prev.has(key)) return prev
          const remaining = new Set(prev)
          remaining.delete(key)
          return remaining
        })
      }

      // Text the editor could not read as a number arrives as NaN. It is staged
      // as NaN, not converted: the unit conversion turns NaN into -1, which
      // would show a number the user never typed.
      const unreadable =
        typeof event.newValue === 'number' && Number.isNaN(event.newValue)
      const stagedValue =
        isMonth && !unreadable
          ? toMetricValue(
              (event.row as unknown as Record<string, unknown>)[field],
              event.newValue,
              'mm',
              units,
            )
          : event.newValue

      // Typing into a staged draft stages the value — it is not written until
      // the toolbar Save runs, exactly like an edit to an existing row. The ref
      // is written straight away as well: Save blurs the open editor, which
      // commits through here in the same tick, where state would still be stale.
      if (isDraftId(rowId)) {
        const next = draftsRef.current.map((draft) =>
          draft.id === rowId
            ? {
                ...draft,
                values: {
                  ...draft.values,
                  [field]: stagedValue as string | number,
                },
              }
            : draft,
        )
        draftsRef.current = next
        setDrafts(next)
        if (field === 'description' && String(event.newValue ?? '').trim()) {
          setInvalidRowIds((prev) => {
            if (!prev.has(rowId)) return prev
            const remaining = new Set(prev)
            remaining.delete(rowId)
            return remaining
          })
        }
        return false as const
      }

      // The chip row carries the "+ Add …" affordance and holds no value.
      if (rowId === ADD_ROW_ID) return false as const

      if (event.newValue === event.oldValue) return false as const

      updatePendingEdits((prev) => {
        const map = new Map(prev)
        map.set(rowId, { ...(map.get(rowId) ?? {}), [field]: stagedValue })
        return map
      })
      return false as const
    },
    [units, noteUserEdit, updatePendingEdits],
  )

  // Enter needs no handling of its own: the editors commit on Enter, which
  // stages the value through `handleCellValueChanged` like any other edit.

  // --- Bulk save / cancel ---------------------------------------------------

  const performSave = useCallback(async (options?: GuidedSaveOptions) => {
    // Commits the open editor before the staged rows are read. Not a
    // `sleep(0)`: a macrotask is throttled to one callback a minute in a
    // hidden tab, which parked the save (SCRUM-1032 QC, 6 Oct). From the
    // autosave with an editor still open, that row is skipped instead and
    // its editor left alone (SCRUM-1032 QC, 30 Oct).
    const heldRowId = commitOrHoldOpenEditor(gridApiRef.current, options)

    const stagedDrafts = draftsRef.current.filter(
      (draft) => draft.id !== heldRowId,
    )

    // Every edit on a row the grid has fetched is sent, including one a search
    // now hides — skipping those reported a clean save while the value never
    // reached the server. An edit on a row an unfiltered list no longer has
    // (deleted, here or elsewhere) cannot land: it is dropped rather than
    // 404ing on every pass and holding the step for good.
    const { send: edits, gone } = splitEditsForSave(
      heldRowId
        ? withoutKey(pendingEditsRef.current, heldRowId)
        : pendingEditsRef.current,
      new Set(knownRowsRef.current.keys()),
      listCompleteRef.current,
    )
    if (gone.length > 0)
      updatePendingEdits((prev) =>
        gone.reduce((map, id) => withoutKey(map, id), prev),
      )

    // Every staged row is validated before anything is written: a half-applied
    // save would leave the user guessing which rows landed.
    const unnamed = stagedDrafts
      .filter((draft) => !String(draft.values.description ?? '').trim())
      .map((draft) => draft.id)

    /**
     * SCRUM-1032 QC item 14 — a saved row that has had its description cleared.
     *
     * Only new rows were checked, so clearing the description on an existing
     * one went to the server as a blank and came back as a row with no name.
     */
    const blankedEdits = edits
      .filter(([id, edit]) => {
        if (!('description' in (edit as any))) return false
        return !String((edit as any).description ?? '').trim()
      })
      .map(([id]) => id)

    const invalid = [...unnamed, ...blankedEdits]

    /**
     * SCRUM-1032 round 7, item 9 — months outside 0–65 mm, or not numbers.
     *
     * The grid used to write -5 as 0, "abc" as 0 and 9999 as typed. Every
     * staged month is checked, on new rows and on edits to saved ones, and the
     * save is refused as a whole like the description check above.
     */
    const monthCells = [
      ...stagedDrafts.map((draft) => [draft.id, draft.values] as const),
      ...edits,
    ].flatMap(([id, values]) =>
      invalidEtMonths(values as Record<string, unknown>, MONTH_FIELDS).map(
        (month) => ({ id, month }),
      ),
    )

    if (invalid.length > 0 || monthCells.length > 0) {
      setInvalidRowIds(new Set(invalid))
      setInvalidMonthCells(
        new Set(monthCells.map(({ id, month }) => `${id}::${month}`)),
      )
      if (invalid.length > 0) {
        // Named, not the bare "is required" this used to show — that key is a
        // suffix, and on its own it left the user hunting for which field it
        // meant. The same shape the pipe tree already uses.
        toast.error(
          `${t('formsLabelsDescription')} ${t('commonFormsValidationIsRequired')}`,
        )
      }
      if (monthCells.length > 0) {
        // The range is given in the user's own unit. A fixed id, so the
        // autosave and Next refusing the same values do not stack toasts.
        toast.error(
          t('etReferenceMonthOutOfRange', {
            month: t(MONTH_LABEL_KEYS[monthCells[0].month]),
            min: units.convertFromMetricV2(ET_MONTH_MIN_MM, 'mm'),
            max: units.convertFromMetricV2(ET_MONTH_MAX_MM, 'mm'),
            unit: etUnit,
          }),
          { id: 'et-reference-month-range' },
        )
      }
      const refusedRows = new Set([
        ...invalid,
        ...monthCells.map(({ id }) => String(id)),
      ])
      // A non-zero `failed` keeps the page in bulk edit, so the batch survives —
      // and now also holds the wizard on this step (see runGuidedSaves).
      return {
        failed: refusedRows.size,
        total: stagedDrafts.length + edits.length,
      }
    }
    setInvalidRowIds(new Set())
    setInvalidMonthCells(new Set())

    // Thunks, not promises: firing every request up front sent the whole batch
    // in parallel, and this API returns 500 under concurrent writes.
    const updateTasks = edits.map(([id, edit]) => () => {
      // From the fetched rows, not the listed ones: a search may hide it.
      const row = knownRowsRef.current.get(id)!
      return etReferenceDataConfig.update({
        id: Number(id),
        ...buildPayload(row, edit),
      } as any)
    })
    // The staged values are handed to the payload builder as the row, the same
    // way the inline create did, so the create keeps its existing shape.
    const createTasks = stagedDrafts.map(
      (draft) => () =>
        etReferenceDataConfig.create(
          buildPayload(
            { ...blankRow(), ...draft.values } as EtReferenceRow,
            {},
          ) as any,
        ),
    )

    const tasks = [...updateTasks, ...createTasks]
    if (tasks.length === 0) return { failed: 0, total: 0 }

    // One at a time, with a retry: the API returns 500 when writes arrive
    // concurrently, which silently dropped most of a large batch.
    let lastError: any = null
    const failedTaskIndexes = new Set<number>()

    for (const [index, task] of tasks.entries()) {
      let saved = false

      for (let attempt = 0; attempt < SAVE_RETRY_LIMIT && !saved; attempt++) {
        try {
          await task()
          saved = true
        } catch (error: any) {
          lastError = error
          // A 4xx is the server refusing the value: resending cannot help.
          if (!isRetryableApiError(error)) break
          if (attempt < SAVE_RETRY_LIMIT - 1) await sleep(SAVE_RETRY_DELAY_MS)
        }
      }

      if (!saved) failedTaskIndexes.add(index)
      await sleep(SAVE_REQUEST_GAP_MS)
    }

    // Retire what landed from the CURRENT staged state, not from the copy
    // taken when the save began: a row added or a month typed while the
    // requests were out is newer than that copy and must survive. Failed
    // edits and drafts stay staged as they are (SCRUM-1032 review, round 7).
    // Edits on unlisted rows were not sent, so they are not touched either —
    // the old rebuild from `edits` alone dropped them.
    const landedEdits = edits.filter((_, index) => !failedTaskIndexes.has(index))
    updatePendingEdits((prev) => retireSavedEdits(prev, landedEdits))

    // The creates trail the updates in `tasks`, so draft N is task N + offset.
    // Re-staging a draft that landed would create it a second time.
    const createOffset = updateTasks.length
    const landedDraftIds = stagedDrafts
      .filter((_, index) => !failedTaskIndexes.has(index + createOffset))
      .map((draft) => draft.id)
    draftsRef.current = retireCreatedDrafts(draftsRef.current, landedDraftIds)
    setDrafts(draftsRef.current)

    const failed = failedTaskIndexes.size

    if (failed === 0) {
      // SCRUM-1032: inside the wizard the step shows one status for every
      // grid on it, so six chips added one at a time no longer raise six
      // toasts. On /config, Save is a deliberate act and still confirms.
      if (!isGuidedSetup)
        toast.success(
          createTasks.length > 0 && updateTasks.length === 0
            ? t('addEtReferenceSuccess')
            : t('updateEtReferenceSuccess'),
        )
    } else if (failed === tasks.length) {
      // The server's reason, under one id so a repeat replaces it.
      toast.error(getApiErrorMessage(lastError, t('somethingWentWrong')), {
        id: 'et-reference-save-error',
      })
    } else {
      toast.error(t('bulkSavePartialError', { failed, total: tasks.length }), {
        id: 'et-reference-save-error',
      })
    }

    if (failed < tasks.length) refresh()

    return { failed, total: tasks.length }
  }, [
    buildPayload,
    blankRow,
    t,
    refresh,
    units,
    etUnit,
    updatePendingEdits,
    isGuidedSetup,
  ])

  // SCRUM-1032 QC items 12 / 15b — one save at a time. The pause autosave
  // and the wizard's Next flush both call this, and two overlapping runs
  // read the same staged rows and write them twice.
  const { save: handleSave, isSaving } = useSerialisedSave(performSave)

  /** A deleted row takes its staged edit with it; nothing is left "held". */
  const forgetRow = useCallback(
    (id: string) => {
      knownRowsRef.current.delete(String(id))
      updatePendingEdits((prev) => withoutKey(prev, String(id)))
    },
    [updatePendingEdits],
  )

  const handleCancel = useCallback(() => {
    updatePendingEdits(() => new Map())
    draftsRef.current = []
    setDrafts([])
    setInvalidRowIds(new Set())
    setInvalidMonthCells(new Set())
    draftIdRef.current = DRAFT_ID_START
    refresh()
  }, [refresh, updatePendingEdits])

  useEffect(() => {
    actionsRef.current = {
      save: handleSave,
      cancel: handleCancel,
      refresh,
      hasPendingEdits: () =>
        pendingEditsRef.current.size > 0 || draftsRef.current.length > 0,
      forgetRow,
    }
    return () => {
      actionsRef.current = null
    }
  }, [actionsRef, handleSave, handleCancel, refresh, forgetRow])

  // Delete only. Row-level save / cancel controls are gone: the toolbar's Save
  // commits every pending change including the staged rows, and Cancel discards
  // them, so there is one place to look for both.
  const ActionsCellRenderer = useCallback(
    ({ row }: GridCellCtx<EtReferenceRow>) => {
      if (!row || row.id === ADD_ROW_ID) return null
      if (!bulkEditMode) return null

      // A draft gets the same ✕ as a saved row, so one staged item can be
      // dropped without cancelling the whole batch. It has never been written,
      // so it is discarded locally instead of going through the delete modal.
      const isDraft = isDraftId(row.id)

      return (
        <Box data-no-cell-edit>
          <DeleteIconButton
            disabled={isViewOnly}
            isDark={isDark}
            onClick={() => {
              if (isViewOnly) return
              if (isDraft) handleDiscardDraft(String(row.id))
              else onDeleteItem?.(String(row.id), row.description)
            }}
          />
        </Box>
      )
    },
    [bulkEditMode, isViewOnly, isDark, onDeleteItem, handleDiscardDraft],
  )

  const isRowEditable = useCallback(
    ({ row }: GridCellCtx<EtReferenceRow>) =>
      row.id === ADD_ROW_ID ? false : bulkEditMode && !isViewOnly,
    [bulkEditMode, isViewOnly],
  )

  const cellClassFor = useCallback(
    (field: string) =>
      ({ row }: GridCellCtx<EtReferenceRow>) => {
        const list: string[] = []
        const rowId = String(row.id)
        // A draft takes the whole-row `new` tint instead of the per-cell
        // editable ring, so the two signals stay distinguishable.
        if (
          bulkEditMode &&
          !isViewOnly &&
          row.id !== ADD_ROW_ID &&
          !isDraftId(rowId)
        ) {
          list.push(gridCellClasses.editable)
        }
        if (field in (pendingEdits.get(rowId) ?? {})) {
          list.push(gridCellClasses.changed)
        }
        if (field === 'description' && invalidRowIds.has(rowId)) {
          list.push(gridCellClasses.invalid)
        }
        if (invalidMonthCells.has(`${rowId}::${field}`)) {
          list.push(gridCellClasses.invalid)
        }
        return list
      },
    [bulkEditMode, isViewOnly, pendingEdits, invalidRowIds, invalidMonthCells],
  )

  // A row still missing its description cannot be written — `handleSave`
  // rejects it and marks the cell — so autosave waits rather than raising the
  // same complaint every few seconds while the user is still typing it.
  const draftsReadyToSave = drafts.every((draft) =>
    String(draft.values?.description ?? '').trim(),
  )

  useGuidedAutosave({
    enabled: isGuidedSetup && bulkEditMode && !isViewOnly && draftsReadyToSave,
    hasPendingChanges: pendingEdits.size > 0 || drafts.length > 0,
    // User edits only: see useUserEditVersion. Correcting a refused month
    // is an edit, which is what asks the autosave to try again.
    version: userEditVersion,
    save: handleSave,
    keepsEditorOpen: true,
    // An open editor is the user mid-value; the save would close it.
    isEditing: () => gridApiRef.current?.isEditingCell() ?? false,
    isBusy: isSaving,
  })

  const columns = useMemo<GridColumn<EtReferenceRow>[]>(
    () => [
      {
        field: 'description',
        headerName: t('formsLabelsDescription'),
        minWidth: 180,
        flex: 1,
        pinned: 'left',
        editable: isRowEditable,
        editor: 'text',
        cellRenderer: ({ row, value }) => {
          if (row.id === ADD_ROW_ID) {
            return (
              <Box
                component="span"
                data-no-cell-edit
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '2px 12px',
                  height: '32px',
                  borderRadius: '8px',
                  border: isViewOnly
                    ? '1px dashed #e0e0e0'
                    : `1px dashed ${
                        isDark
                          ? 'rgba(52, 211, 153, 0.5)'
                          : 'rgba(0, 71, 68, 0.5)'
                      }`,
                  cursor: isViewOnly ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: isViewOnly
                    ? '#bdbdbd'
                    : isDark
                      ? '#5fe0ad'
                      : '#004744',
                }}
                onClick={handleAddDraft}
              >
                {t('dataConfigAddETReference')}
              </Box>
            )
          }
          return <>{value as string}</>
        },
        cellClass: cellClassFor('description'),
      },

      // The twelve monthly values, shown in the user's unit.
      ...MONTH_FIELDS.map<GridColumn<EtReferenceRow>>((month) => ({
        field: month,
        // The break is written in rather than left to the column width: at 84px
        // the longer months wrapped and the shorter ones did not, so the header
        // row read as a mistake.
        headerName: `${t(MONTH_LABEL_KEYS[month])}\n(${etUnit})`,
        width: 84,
        numeric: true,
        editable: isRowEditable,
        editor: 'number',
        // `clamp: false`: an out-of-range or unreadable value reaches the save
        // and is refused there with a reason, instead of quietly becoming 0.
        editorParams: { min: 0, precision: 2, clamp: false },
        // The conversion lives here rather than in the renderer so the cell,
        // the editor's seed value and sorting all agree on one number. With a
        // renderer-only conversion the cell read 0.12 in while double-clicking
        // it opened the editor on the raw 3 mm, and whatever was typed was then
        // treated as inches on save.
        valueGetter: (row: EtReferenceRow) => {
          // Unreadable text is staged as NaN; the conversion would show it as
          // -1. Blank, with the cell marked, is what the user actually typed.
          const raw = (row as unknown as Record<string, unknown>)?.[month]
          if (typeof raw === 'number' && Number.isNaN(raw)) return ''
          return unitValueGetter<EtReferenceRow>(month, 'mm', units)(row)
        },
        cellRenderer: ({ value }) => {
          // A staged row holds exactly what was typed and nothing else — a
          // blank month is blank, not a zero the user never entered. Saved rows
          // keep a stored zero visible: the old table showed "-" for it, which
          // read as "never set".
          if (value === undefined || value === null || value === '')
            return <></>
          return <>{String(value)}</>
        },
        cellClass: cellClassFor(month),
      })),

      // Delete is the only action and it only applies in bulk edit, so the
      // pinned column is dropped entirely outside it — an always-present frozen
      // column leaves an empty strip and a divider against the right edge.
      ...(bulkEditMode
        ? [
            {
              field: 'actions',
              headerName: '',
              // One 30px icon button. 96 left a wide empty band beside it, and
              // on a twelve-month row that space is scarce. Matches the width
              // the Plants, Soil and Emitter lists settled on.
              width: 52,
              pinned: 'right' as const,
              sortable: false,
              resizable: false,
              editable: false,
              cellRenderer: ActionsCellRenderer,
              cellStyle: {
                justifyContent: 'flex-end',
                paddingRight: '12px',
                overflow: 'visible',
              },
            } satisfies GridColumn<EtReferenceRow>,
          ]
        : []),
    ],
    [
      t,
      etUnit,
      isRowEditable,
      cellClassFor,
      isViewOnly,
      isDark,
      units,
      bulkEditMode,
      ActionsCellRenderer,
      handleAddDraft,
    ],
  )

  const rowClass = useCallback(
    ({ row }: { row: EtReferenceRow }) =>
      isDraftId(row.id) ? gridRowClasses.new : undefined,
    [],
  )

  return (
    <DataGrid<EtReferenceRow>
      columns={columns}
      rows={rowData}
      loading={!loadedOnce}
      getRowId={getRowId}
      postSort={postSort}
      apiRef={gridApiRef}
      rowHeight={40}
      headerHeight={42}
      // Twelve month columns, each carrying its unit: "Jan (mm)" does not fit
      // an 84px column and was ellipsed to "Jan (m…", which is neither the
      // month nor the unit. Wrapped, the unit drops to a second line and both
      // are readable.
      wrapHeaderText
      editMode="singleClick"
      rowClass={rowClass}
      onCellValueChanged={handleCellValueChanged}
    />
  )
}

export default EtReferenceTable
